import { useMemo } from 'react';
import { useSystemSettings } from './useSystemSettings';
import { ALL_PACKAGES, CATEGORY_DELIVERABLES, type PackageDefinition } from '@/config/packages';
import { CARD_SUBSCRIPTIONS, type CardSubscription } from '@/config/cardSubscriptions';
import { supabase } from '@/integrations/supabase/client';

type YouDoItem = string | { main: string; sub: string[] };

interface DeliverableConfig {
  youGet: string[];
  youDo: YouDoItem[];
  effortLabel: string;
}

interface PricingConfigEntry {
  id: string;
  price?: number;
  name?: string;
  description?: string;
  features?: string[];
  maxItemsPerUpload?: number;
  monthlyUploadLimit?: number;
}

interface PricingConfig {
  packages?: PricingConfigEntry[];
  subscriptions?: PricingConfigEntry[];
  deliverables?: Record<string, DeliverableConfig>;
}

function mergePackages(cfg: PricingConfig): PackageDefinition[] {
  if (!cfg.packages || !Array.isArray(cfg.packages)) return [...ALL_PACKAGES];
  return ALL_PACKAGES.map(pkg => {
    const o = cfg.packages!.find(p => p.id === pkg.id);
    if (!o) return pkg;
    const price = o.price ?? pkg.price;
    return { ...pkg, price, priceDisplay: `${price}€`, name: o.name ?? pkg.name, description: o.description ?? pkg.description, features: o.features ?? pkg.features };
  });
}

function mergeSubscriptions(cfg: PricingConfig): CardSubscription[] {
  if (!cfg.subscriptions || !Array.isArray(cfg.subscriptions)) return [...CARD_SUBSCRIPTIONS];
  return CARD_SUBSCRIPTIONS.map(sub => {
    const o = cfg.subscriptions!.find(s => s.id === sub.id);
    if (!o) return sub;
    const price = o.price ?? sub.price;
    return { ...sub, price, priceDisplay: `${price}€/Monat`, name: o.name ?? sub.name, description: o.description ?? sub.description, maxItemsPerUpload: o.maxItemsPerUpload ?? sub.maxItemsPerUpload, monthlyUploadLimit: o.monthlyUploadLimit ?? sub.monthlyUploadLimit };
  });
}

function mergeDeliverables(cfg: PricingConfig): Record<'S' | 'M' | 'L', DeliverableConfig> {
  const base = { ...CATEGORY_DELIVERABLES };
  if (cfg.deliverables) {
    for (const key of ['S', 'M', 'L'] as const) {
      const o = cfg.deliverables[key];
      if (o) {
        base[key] = {
          youGet: o.youGet ?? base[key].youGet,
          youDo: o.youDo ?? base[key].youDo,
          effortLabel: o.effortLabel ?? base[key].effortLabel,
        };
      }
    }
  }
  return base;
}

/**
 * Standalone async function to load dynamic pricing (for use outside React components).
 */
export async function loadDynamicPricing(): Promise<{ packages: PackageDefinition[]; subscriptions: CardSubscription[]; deliverables: Record<'S' | 'M' | 'L', DeliverableConfig>; subscriptionsEnabled: boolean }> {
  try {
    const { data } = await supabase
      .from('system_settings')
      .select('setting_key, setting_value')
      .in('setting_key', ['pricing_config', 'subscriptions_enabled']);

    let cfg: PricingConfig = {};
    let subscriptionsEnabled = true;

    for (const row of data || []) {
      if (row.setting_key === 'pricing_config') {
        cfg = (row.setting_value as unknown as PricingConfig) || {};
      } else if (row.setting_key === 'subscriptions_enabled') {
        const val = row.setting_value as { enabled?: boolean } | null;
        subscriptionsEnabled = val?.enabled !== false;
      }
    }

    return {
      packages: mergePackages(cfg),
      subscriptions: mergeSubscriptions(cfg),
      deliverables: mergeDeliverables(cfg),
      subscriptionsEnabled,
    };
  } catch {
    return { packages: ALL_PACKAGES, subscriptions: CARD_SUBSCRIPTIONS, deliverables: { ...CATEGORY_DELIVERABLES }, subscriptionsEnabled: true };
  }
}

/**
 * Loads pricing config from the shared system_settings batch query.
 * No longer makes its own Supabase call.
 */
export function useDynamicPricing() {
  const { data: settings, isLoading: loading } = useSystemSettings();

  const cfg = useMemo(() => {
    return (settings?.pricing_config as unknown as PricingConfig) || {};
  }, [settings?.pricing_config]);

  const subscriptionsEnabled = settings?.subscriptions_enabled !== false;

  const packages = useMemo(() => mergePackages(cfg), [cfg]);
  const subscriptions = useMemo(() => mergeSubscriptions(cfg), [cfg]);
  const deliverables = useMemo(() => mergeDeliverables(cfg), [cfg]);

  const getPackage = (id: string) => packages.find(p => p.id === id);
  const getSubscription = (id: string) => subscriptions.find(s => s.id === id);

  const priceRange = useMemo(() => {
    const prices = packages.filter(p => !p.disabled).map(p => p.price);
    if (prices.length === 0) return '';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return `${min}€ bis ${max}€`;
  }, [packages]);

  return { packages, subscriptions, deliverables, loading, getPackage, getSubscription, priceRange, subscriptionsEnabled };
}
